import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Phone, MapPin, Sparkles, ArrowRight } from "lucide-react";
import LogoText from "./LogoText";
import { useSiteConfig } from "@/hooks/useSiteConfig";
import { useActiveTransferDestinations } from "@/hooks/useTransferDestinations";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { getConfig } = useSiteConfig();
  const { data: destinations = [] } = useActiveTransferDestinations();
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const popularDestinations = destinations
    .sort((a, b) => a.display_order - b.display_order)
    .slice(0, 6);

  const navigationItems = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Transfers", href: "/transfers" },
    { name: "Destinations", href: "/traslados" },
    { name: "Tours", href: "/tours" },
    { name: "Blog", href: "/blog" },
    { name: "Contact", href: "/contact" },
  ];

  const isActive = (href: string) => location.pathname === href;

  return (
    <header className={`sticky top-0 z-50 transition-all duration-500 ${
      isScrolled 
        ? 'bg-background/95 backdrop-blur-xl shadow-2xl shadow-primary/10 border-b border-primary/10' 
        : 'bg-background/80 backdrop-blur-md shadow-lg border-b border-border/50'
    }`}>
      {/* Gradient Top Border */}
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-tropical opacity-80"></div>
      
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo with Hover Effect */}
          <Link 
            to="/" 
            className="flex items-center space-x-2 group transition-transform duration-300 hover:scale-105"
          >
            <LogoText className="h-16 drop-shadow-sm" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            <NavigationMenu>
              <NavigationMenuList className="gap-1">
                {navigationItems.map((item) => {
                  if (item.name === "Destinations") {
                    return (
                      <NavigationMenuItem key={item.name}>
                        <NavigationMenuTrigger 
                          className={`group relative font-medium text-sm uppercase tracking-wide bg-transparent px-4 py-2 transition-all duration-300 ${
                            isActive(item.href) 
                              ? "text-primary" 
                              : "text-foreground hover:text-primary"
                          }`}
                        >
                          {item.name}
                          {/* Gradient Underline */}
                          <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary via-secondary to-tropical transition-all duration-300 rounded-full ${
                            isActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'
                          }`} />
                        </NavigationMenuTrigger>
                        <NavigationMenuContent className="z-50">
                          <div className="w-[650px] bg-background/98 backdrop-blur-xl border border-primary/20 rounded-2xl shadow-2xl shadow-primary/10 p-6 animate-fade-in">
                            {/* Header */}
                            <div className="mb-5 pb-4 border-b border-border/50">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                                  <MapPin className="h-4 w-4 text-white" />
                                </div>
                                <h3 className="text-lg font-bold text-foreground">Popular Destinations</h3>
                              </div>
                              <p className="text-sm text-muted-foreground">Explore our most requested transfer routes</p>
                            </div>
                            
                            {/* Destinations Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              {popularDestinations.map((destination) => (
                                <Link
                                  key={destination.id}
                                  to={`/traslados/${destination.slug}`}
                                  className="group/item flex items-center gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-primary/10 hover:to-secondary/5 transition-all duration-300 border border-transparent hover:border-primary/20"
                                >
                                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-secondary/10 flex items-center justify-center group-hover/item:from-primary group-hover/item:to-secondary transition-all duration-300">
                                    <MapPin className="h-5 w-5 text-primary group-hover/item:text-white transition-colors" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-foreground group-hover/item:text-primary transition-colors">
                                      {destination.name}
                                    </div>
                                    {destination.from_airport_price && (
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        From <span className="text-tropical font-semibold">${destination.from_airport_price}</span>
                                      </div>
                                    )}
                                  </div>
                                  <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover/item:opacity-100 group-hover/item:translate-x-1 transition-all" />
                                </Link>
                              ))}
                            </div>
                            
                            {/* View All Link */}
                            <Link
                              to="/traslados"
                              className="mt-5 flex items-center justify-center gap-2 text-sm text-primary hover:text-secondary font-medium transition-colors group/link"
                            >
                              <span>View All Destinations</span>
                              <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                            </Link>
                          </div>
                        </NavigationMenuContent>
                      </NavigationMenuItem>
                    );
                  }
                  
                  return (
                    <NavigationMenuItem key={item.name}>
                      <Link
                        to={item.href}
                        className={`group relative font-medium text-sm uppercase tracking-wide px-4 py-2 transition-all duration-300 block ${
                          isActive(item.href) 
                            ? "text-primary" 
                            : "text-foreground hover:text-primary"
                        }`}
                      >
                        {item.name}
                        {/* Gradient Underline Animation */}
                        <span className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-gradient-to-r from-primary via-secondary to-tropical transition-all duration-300 rounded-full ${
                          isActive(item.href) ? 'w-full' : 'w-0 group-hover:w-full'
                        }`} />
                      </Link>
                    </NavigationMenuItem>
                  );
                })}
              </NavigationMenuList>
            </NavigationMenu>
          </nav>

          {/* Contact Info & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a 
              href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`}
              className="group flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:scale-110 transition-all">
                <Phone className="h-4 w-4 text-primary group-hover:text-white transition-colors" />
              </div>
              <span className="font-medium">{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
            </a>
            <Link to="/transfers">
              <Button className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-105 font-semibold px-6">
                <Sparkles className="w-4 h-4 mr-2" />
                Book Now
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button 
                variant="outline" 
                size="icon" 
                className="border-primary/20 text-foreground hover:bg-primary/10 hover:border-primary/40 transition-all"
              >
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-[320px] sm:w-[400px] bg-background/98 backdrop-blur-xl text-foreground border-l border-primary/20 p-0"
            >
              {/* Mobile Menu Header */}
              <div className="p-6 border-b border-border/50 bg-gradient-to-r from-primary/5 to-secondary/5">
                <LogoText className="h-14" />
              </div>
              
              {/* Mobile Navigation */}
              <nav className="flex flex-col p-6 space-y-1">
                {navigationItems.map((item, index) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`group flex items-center justify-between font-medium text-lg py-3 px-4 rounded-xl transition-all duration-300 ${
                      isActive(item.href) 
                        ? "text-primary bg-primary/10" 
                        : "text-foreground hover:text-primary hover:bg-primary/5"
                    }`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <span>{item.name}</span>
                    <ArrowRight className={`w-4 h-4 transition-all duration-300 ${
                      isActive(item.href) 
                        ? 'opacity-100 translate-x-0' 
                        : 'opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0'
                    }`} />
                  </Link>
                ))}
              </nav>

              {/* Mobile Menu Footer */}
              <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-border/50 bg-gradient-to-r from-primary/5 to-secondary/5 space-y-4">
                <a 
                  href={`tel:${getConfig('contact_phone', '+1 (809) 123-4567')}`}
                  className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <span className="font-medium">{getConfig('contact_phone', '+1 (809) 123-4567')}</span>
                </a>
                <Link to="/transfers" onClick={() => setIsOpen(false)} className="block">
                  <Button className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-white shadow-lg font-semibold py-6 text-base">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Book Now
                  </Button>
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
